// Public welcome page: the link Tim sends to possible alpha testers.
import { useQuery } from '@tanstack/react-query';
import { Clock, Coins, Factory, Flag, type LucideIcon, Rocket, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { endpoints } from '../api/endpoints';
import { DiscordLink } from '../components/DiscordLink';
import { buttonClass } from '../components/ui';
import { DISCORD_INVITE_URL } from '../links';
import { useSession } from '../state/session';
import { VERSION_LABEL } from '../version';

const STEPS: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: Factory,
    title: 'Build',
    text: 'You start with one home system. Upgrade mines, gas harvesters and farms to produce Metal, Gas and Supplies, and storages to hold them.',
  },
  {
    icon: Coins,
    title: 'Trade',
    text: 'Sell resources at your Trading Port for Credits. Credits pay for System Claims — the only way to take a new system.',
  },
  {
    icon: Rocket,
    title: 'Build a fleet',
    text: 'Your Space Dock builds Interceptors, Transports, Frigates and Cruisers. Every class has its job in battle.',
  },
  {
    icon: Flag,
    title: 'Expand',
    text: 'Scout your neighbours, raid them for resources, claim free systems and capture rival ones. Fleets fly in real time, so a defender sees an attack coming.',
  },
  {
    icon: Users,
    title: 'Ally',
    text: 'Join an alliance: share warp gates and intel, send reinforcements and plan attacks that arrive at the same second.',
  },
];

export function WelcomeScreen() {
  const status = useSession((s) => s.status);
  const info = useQuery({ queryKey: ['registration-info'], queryFn: endpoints.registrationInfo });
  const signedIn = status === 'authenticated';

  return (
    <main className="starfield min-h-dvh px-4 py-12 sm:py-20">
      <div className="mx-auto w-full max-w-3xl space-y-14">
        <header className="space-y-6 text-center">
          <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-accent">
            Closed alpha · testers wanted
          </p>
          <h1 className="font-display text-4xl font-bold uppercase tracking-[0.18em] text-info sm:text-6xl">
            Stars &amp; Ships
          </h1>
          <p className="mx-auto max-w-xl text-lg text-text">
            Build a star empire, trade, raid and conquer — together with your alliance, in a galaxy
            that never stops.
          </p>
          <p className="mx-auto max-w-xl text-sm text-muted">
            A persistent real-time strategy game for your browser, on phone and desktop. Nothing to
            install.
          </p>
          <div className="mx-auto flex max-w-sm flex-col gap-3">
            {signedIn ? (
              <Link className={buttonClass('primary')} to="/galaxies">
                Continue playing
              </Link>
            ) : (
              <Link className={buttonClass('primary')} to="/register">
                Join the alpha
              </Link>
            )}
            <DiscordLink />
            {signedIn ? null : (
              <p className="text-sm text-muted">
                Already playing?{' '}
                <Link className="text-info underline-offset-2 hover:underline" to="/login">
                  Log in
                </Link>
              </p>
            )}
          </div>
        </header>

        <Section title="How it works">
          <ol className="grid gap-3 sm:grid-cols-2">
            {STEPS.map(({ icon: Icon, title, text }, i) => (
              <li
                key={title}
                className={`hud-cut border border-line bg-panel/90 p-4 ${i === STEPS.length - 1 ? 'sm:col-span-2' : ''}`}
              >
                <h3 className="flex items-center gap-2 font-display text-base font-bold uppercase tracking-[0.1em] text-text">
                  <Icon aria-hidden className="size-5 text-accent" />
                  {title}
                </h3>
                <p className="mt-2 text-sm text-muted">{text}</p>
              </li>
            ))}
          </ol>
          <div className="mt-3 flex gap-3 hud-cut border border-line bg-panel/90 p-4">
            <Clock aria-hidden className="mt-0.5 size-5 shrink-0 text-info" />
            <p className="text-sm text-muted">
              <span className="font-medium text-text">Play a few minutes, a few times a day.</span>{' '}
              Upgrades, ships and flights take minutes to hours, and the galaxy keeps going while
              you are away. Planning and teamwork win, not time spent online.
            </p>
          </div>
        </Section>

        <Section title="The alpha">
          <ul className="space-y-2 text-sm text-muted">
            <Bullet>One small galaxy with a few dozen commanders.</Bullet>
            <Bullet>Free — there is no real money in the game.</Bullet>
            <Bullet>
              An early build: numbers will change and you will find bugs. Tell us in the game (Menu
              → Send feedback){DISCORD_INVITE_URL ? ' or on Discord' : ''}.
            </Bullet>
            {info.data?.inviteCodeRequired ? (
              <Bullet>
                Signing up needs an invite code —{' '}
                {DISCORD_INVITE_URL
                  ? 'you get it on our Discord.'
                  : 'you get it from the person who invited you.'}
              </Bullet>
            ) : null}
          </ul>
        </Section>

        {DISCORD_INVITE_URL ? (
          <Section title="Community">
            <div className="hud-cut border border-line bg-panel/90 p-4 text-center">
              <p className="text-sm text-muted">
                Meet the other commanders, find an alliance, ask questions and follow the alpha.
              </p>
              <div className="mx-auto mt-4 max-w-sm">
                <DiscordLink />
              </div>
            </div>
          </Section>
        ) : null}

        <p className="text-center text-xs text-muted">{VERSION_LABEL}</p>
      </div>
    </main>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-muted">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2">
      <span aria-hidden className="text-accent">
        ▸
      </span>
      <span>{children}</span>
    </li>
  );
}
